import { arrayFindIndex, arrayFind, swap, findFirstParentOf, valueEquals } from '../../utils/util';
import { getCell, getColumnByCell, getRowIdentity } from './util';
import { getStyle, hasClass, removeClass, addClass } from '../../utils/dom';
import ZmCheckbox from '../checkbox';
import ZmTooltip from '../tooltip';
import debounce from 'throttle-debounce/debounce';
import LayoutObserver from './layout-observer';
import { mapStates } from './store/helper';
import Draggable from 'vuedraggable';
import AriaUtils from '../../utils/aria-utils';
import { raf } from '../../utils/scroll';

export default {
  name: 'ZmTableBody',

  mixins: [LayoutObserver],

  components: {
    ZmCheckbox,
    ZmTooltip,
    Draggable
  },

  props: {
    store: {
      type: Object,
      required: true
    },
    stripe: Boolean,
    context: Object,
    rowClassName: [String, Function],
    rowStyle: [Object, Function],
    fixed: String
  },

  created() {
    if (this.table.useMemo) {
      if (!this.table.rowKey) {
        throw new Error('for useMemo, row-key is required.');
      }
      this.vnodeCache = [];
    }
  },

  render(h) {
    const { store, data = [], treeIndent, columns, fixedLeafColumns, rightFixedLeafColumns, firstDefaultColumnIndex, stripe, flip, fixed, leftFixedLeafCount, columnsCount, rightFixedLeafCount, hasExpandColumn, hasDraggableColumn } = this;
    const { isRowExpanded, assertRowKey } = store;
    const { treeData, lazyTreeNodeMap, childrenColumnName, rowKey, currentRow, expandRows } = store.states;
    const context = this.context || this.table.$vnode.context;
    const { highlightCurrentRow, rowClassName, rowStyle, spanMethod, cellStyle, cellClassName, renderExpanded, forceExpand, ariaLabel, useMemo } = this.table;
    const scrollX = this.table.layout.scrollX;
    const columnsHidden = this.columnsHidden = columns.map((column, index) => this.isColumnHidden({
      flip,
      index,
      fixed,
      leftFixedLeafCount,
      columnsCount,
      rightFixedLeafCount,
      scrollX
    }));
    const disableTransition = store.states.data && store.states.data.length > 0;

    let _columns = this.fixed
      ? this.fixed === 'right'
        ? rightFixedLeafColumns
        : fixedLeafColumns
      : columns;

    _columns = _columns.filter((column) => {
      if (column.type === 'expand' && forceExpand) {
        return false;
      }
      return true;
    });

    return (
      <table
        class={this.getTableClass()}
        cellspacing="0"
        cellpadding="0"
        role="grid"
        aria-label={ariaLabel}
        border="0"
      >
        <colgroup>
          {
            _columns.map(column => <col name={column.id} key={column.id}/>)
          }
        </colgroup>
        {
          hasDraggableColumn
            ? <draggable
              tag="tbody"
              value={data}
              on-input={(value) => {
                store.commit('setData', value);
                this.table.$emit('drag-end', value);
              }}
            >
              {
                data.reduce((acc, row) => {
                  return acc.concat(this.wrappedRowRender(
                    {
                      row,
                      $index: acc.length,
                      store,
                      isRowExpanded,
                      assertRowKey,
                      treeData,
                      lazyTreeNodeMap,
                      childrenColumnName,
                      rowKey,
                      treeIndent,
                      columns: _columns,
                      firstDefaultColumnIndex,
                      columnsHidden,
                      context,
                      stripe,
                      flip,
                      currentRow,
                      expandRows,
                      highlightCurrentRow,
                      rowClassName,
                      rowStyle,
                      spanMethod,
                      cellStyle,
                      cellClassName,
                      renderExpanded,
                      fixed,
                      leftFixedLeafCount,
                      columnsCount,
                      rightFixedLeafCount,
                      scrollX,
                      hasExpandColumn,
                      disableTransition,
                      forceExpand,
                      useMemo
                    }));
                }, [])
              }
              <zm-tooltip
                effect={this.table.tooltipEffect}
                placement="top"
                ref="tooltip"
                content={this.tooltipContent}
                break-word
              ></zm-tooltip>
            </draggable> : <tbody>{
              data.reduce((acc, row) => {
                return acc.concat(this.wrappedRowRender(
                  {
                    row,
                    $index: acc.length,
                    store,
                    isRowExpanded,
                    assertRowKey,
                    treeData,
                    lazyTreeNodeMap,
                    childrenColumnName,
                    rowKey,
                    treeIndent,
                    columns: _columns,
                    firstDefaultColumnIndex,
                    columnsHidden,
                    context,
                    stripe,
                    flip,
                    currentRow,
                    expandRows,
                    highlightCurrentRow,
                    rowClassName,
                    rowStyle,
                    spanMethod,
                    cellStyle,
                    cellClassName,
                    renderExpanded,
                    fixed,
                    leftFixedLeafCount,
                    columnsCount,
                    rightFixedLeafCount,
                    scrollX,
                    hasExpandColumn,
                    disableTransition,
                    forceExpand,
                    useMemo
                  }));
              }, [])
            }
            <zm-tooltip
              effect={this.table.tooltipEffect}
              placement="top"
              ref="tooltip"
              content={this.tooltipContent}
              break-word
            ></zm-tooltip>
            </tbody>
        }
      </table>
    );
  },

  inject: ['table'],

  computed: {
    ...mapStates({
      data: 'data',
      columns: 'columns',
      treeIndent: 'indent',
      fixedLeafColumns: 'fixedLeafColumns',
      rightFixedLeafColumns: 'rightFixedLeafColumns',
      leftFixedLeafCount: 'fixedLeafColumnsLength',
      rightFixedLeafCount: 'rightFixedLeafColumnsLength',
      columnsCount: states => states.columns.length,
      leftFixedCount: states => states.fixedColumns.length,
      rightFixedCount: states => states.rightFixedColumns.length,
      hasExpandColumn: states => states.columns.some(({ type }) => type === 'expand') || states.hiddenColumns.some(({ type }) => type === 'expand'),
      hasDraggableColumn: states => states.columns.some(({ type }) => type === 'draggable') || states.hiddenColumns.some(({ type }) => type === 'draggable')
    }),

    firstDefaultColumnIndex() {
      return arrayFindIndex(this.columns, ({ type }) => type === 'default');
    },

    flip() {
      return this.store.states.flip;
    }
  },

  watch: {
    // don't trigger getter of currentRow in getCellClass. see https://jsfiddle.net/oe2b4hqt/
    // update DOM manually. see https://github.com/ElemeFE/element/pull/13954/files#diff-9b450c00d0a9dec0ffad5a3176972e40
    'store.states.hoverRow'(newVal, oldVal) {
      if (!this.store.states.isComplex || this.$isServer) return;
      raf(() => {
        const rows = this.$el.querySelectorAll('.zm-table__row');
        const oldRow = rows[oldVal];
        const newRow = rows[newVal];
        if (oldRow) {
          removeClass(oldRow, 'hover-row');
        }
        if (newRow) {
          addClass(newRow, 'hover-row');
        }
      });
    },
    'store.states.data'() {
      if (this.table.useMemo) {
        this.vnodeCache = [];
      }
    },
    'store.states.columns'() {
      if (this.table.useMemo) {
        this.vnodeCache = [];
      }
    },
    columnsHidden: {
      sync: true,
      handler(newVal, oldVal) {
        if (this.table.useMemo && !valueEquals(newVal, oldVal)) {
          this.vnodeCache = [];
        }
      }
    },
    flip() {
      if (this.table.useMemo) {
        this.vnodeCache = [];
      }
    }
  },

  data() {
    return {
      tooltipContent: '',
      columnsHidden: []
    };
  },

  methods: {
    getTableClass() {
      const classes = ['zm-table__body'];
      if (this.flip) {
        classes.push('is-flip');
      }
      return classes.join(' ');
    },
    getKeyOfRow({ row, rowIndex, rowKey }) {
      if (rowKey) {
        return getRowIdentity(row, rowKey);
      }
      return rowIndex;
    },

    isColumnHidden({ index, flip, fixed, leftFixedLeafCount, columnsCount, rightFixedLeafCount, scrollX }) {
      if (flip || this.fixed) {
        return false;
      }
      return scrollX && (index < leftFixedLeafCount || (index >= columnsCount - rightFixedLeafCount));
    },

    isRowSelectionChanged(row, memo, selection) {
      const selected = arrayFind(selection, (item) => {
        return item === row;
      });
      const selectedInMemo = arrayFind(memo, (item) => {
        return item === row;
      });
      return selected !== selectedInMemo;
    },

    getSpan({ row, column, rowIndex, columnIndex, spanMethod, columnLength }) {
      let rowspan = 1;
      let colspan = 1;
      if (typeof spanMethod === 'function') {
        const result = spanMethod({
          row,
          column,
          rowIndex,
          columnIndex,
          columnLength
        });
        if (Array.isArray(result)) {
          rowspan = result[0];
          colspan = result[1];
        } else if (typeof result === 'object') {
          rowspan = result.rowspan;
          colspan = result.colspan;
        }
      }
      return { rowspan, colspan };
    },

    getRowStyle({ row, rowIndex, rowStyle }) {
      if (typeof rowStyle === 'function') {
        return rowStyle.call(null, {
          row,
          rowIndex
        });
      }
      return rowStyle || null;
    },

    getRowClass({ row, rowIndex, stripe, flip, currentRow, expandRows, highlightCurrentRow, rowClassName, forceExpand, hasExpandColumn }) {
      const classes = ['zm-table__row'];
      if (highlightCurrentRow && row === currentRow) {
        classes.push('current-row');
      }

      if (flip) {
        classes.push('is-flip');
      }

      if (stripe && rowIndex % 2 === 1) {
        classes.push('zm-table__row--striped');
      }
      if (typeof rowClassName === 'string') {
        classes.push(rowClassName);
      } else if (typeof rowClassName === 'function') {
        classes.push(rowClassName.call(null, {
          row,
          rowIndex
        }));
      }

      if (expandRows.indexOf(row) > -1 && hasExpandColumn) {
        classes.push('expanded');
        if (forceExpand) {
          classes.push('force-expanded');
        }
      }

      return classes;
    },

    getCellStyle({ rowIndex, columnIndex, row, column, cellStyle, flip }) {
      if (typeof cellStyle === 'function') {
        return cellStyle.call(null, {
          rowIndex,
          columnIndex,
          row,
          column
        });
      }
      if (!cellStyle) {
        cellStyle = {};
      }
      if (!flip) {
        cellStyle.maxWidth = `${column.realWidth || column.width}px`;
      }
      return cellStyle;
    },

    getCellClass({ rowIndex, columnIndex, row, column, cellClassName, flip, fixed, leftFixedLeafCount, columnsCount, rightFixedLeafCount, scrollX, columnsHidden }) {
      const classes = [column.id, column.align, column.className];

      if (columnsHidden[columnIndex]) {
        classes.push('is-hidden');
      }

      if (flip) {
        classes.push('is-flip');
      }

      if (typeof cellClassName === 'string') {
        classes.push(cellClassName);
      } else if (typeof cellClassName === 'function') {
        classes.push(cellClassName.call(null, {
          rowIndex,
          columnIndex,
          row,
          column
        }));
      }
      return classes;
      // return classes.join(' ').trim();
    },

    getColspanRealWidth(columns, colspan, index) {
      if (colspan < 1) {
        return columns[index].realWidth;
      }
      const widthArr = columns.map(({ realWidth }) => realWidth).slice(index, index + colspan);
      return widthArr.reduce((acc, width) => acc + width, -1);
    },

    handleCellMouseEnter(event, row) {
      const table = this.table;
      let cell = getCell(event);

      if (cell) {
        const column = getColumnByCell(table, cell);
        const hoverState = table.hoverState = { cell, column, row };
        table.$emit('cell-mouse-enter', hoverState.row, hoverState.column, hoverState.cell, event);
      }

      // 判断是否text-overflow, 如果是就显示tooltip
      const cellChild = event.target.querySelector('.cell');
      if (!(hasClass(cellChild, 'zm-tooltip') && cellChild.childNodes.length)) {
        return;
      }
      // use range width instead of scrollWidth to determine whether the text is overflowing
      // to address a potential FireFox bug: https://bugzilla.mozilla.org/show_bug.cgi?id=1074543#c3
      const range = document.createRange();
      range.setStart(cellChild, 0);
      range.setEnd(cellChild, cellChild.childNodes.length);
      const rangeWidth = range.getBoundingClientRect().width;
      const padding = (parseInt(getStyle(cellChild, 'paddingLeft'), 10) || 0) +
        (parseInt(getStyle(cellChild, 'paddingRight'), 10) || 0);
      if ((rangeWidth + padding > cellChild.offsetWidth || cellChild.scrollWidth > cellChild.offsetWidth) && this.$refs.tooltip) {
        const tooltip = this.$refs.tooltip;

        const determineTooltip = table.determineTooltip && cell.querySelector('.' + table.determineTooltip);
        if (determineTooltip) {
          cell = determineTooltip;
        }

        this.tooltipContent = cell.innerText || cell.textContent;
        tooltip.referenceElm = cell;
        if (tooltip.showPopper) {
          this.$nextTick(() => {
            tooltip.updatePopperReference(cell);
            tooltip.updatePopper();
          });
        }
        tooltip.show();
      }
    },

    handleCellMouseLeave(event) {
      const tooltip = this.$refs.tooltip;
      if (tooltip) {
        tooltip.hide();
      }
      const cell = getCell(event);
      if (!cell) return;

      const oldHoverState = this.table.hoverState || {};
      this.table.$emit('cell-mouse-leave', oldHoverState.row, oldHoverState.column, oldHoverState.cell, event);
    },

    handleMouseEnter: debounce(30, function(index) {
      this.store.commit('setHoverRow', index);
    }),

    handleMouseLeave: debounce(30, function() {
      this.store.commit('setHoverRow', null);
    }),

    handleContextMenu(event, row) {
      this.handleEvent(event, row, 'contextmenu');
    },

    handleKeyDown(event, index) {
      if (!this.hasDraggableColumn) {
        return;
      }
      const keyCode = event.keyCode;
      const keys = AriaUtils.keys;
      if ((event.metaKey || event.ctrlKey) && (keyCode === keys.up || (keyCode === keys.down))) {
        const data = this.data.slice();
        const currentTD = findFirstParentOf(event.target, 'TD');
        const colIndex = currentTD.dataset.index;
        const currentTR = event.currentTarget;
        let nextTD;
        let nextFocusEl;
        let direction;

        if (keyCode === keys.up) {
          if (index > 0) {
            swap(data, index, index - 1);
            this.store.commit('setData', data);
            nextTD = currentTR.previousSibling.children[colIndex];
            direction = 'up';
          }
        } else {
          if (index < data.length - 1) {
            swap(data, index, index + 1);
            this.store.commit('setData', data);
            nextTD = currentTR.nextSibling.children[colIndex];
            direction = 'down';
          }
        }
        if (!nextTD) {
          return;
        }
        this.table.$emit('drag-end', data);

        this.$nextTick(() => {
          if (AriaUtils.isFocusable(nextTD)) {
            nextFocusEl = nextTD;
          } else {
            nextFocusEl = AriaUtils.findFirstFocusEl(nextTD, null, false);
          }
          if (nextFocusEl) {
            if (nextFocusEl.getAttribute('tabindex') === -1) {
              nextFocusEl.setAttribute('tabindex', 0);
            }
            nextFocusEl.focus();
          }
          const grid = this.table.grid;
          if (direction === 'up') {
            grid.focusedRow--;
          } else {
            grid.focusedRow++;
          }
        });

        event.stopPropagation();
        event.preventDefault();
      }
    },

    handleDoubleClick(event, row) {
      this.handleEvent(event, row, 'dblclick');
    },

    handleClick(event, row) {
      this.store.commit('setCurrentRow', row);
      this.handleEvent(event, row, 'click');
    },

    handleEvent(event, row, name) {
      const table = this.table;
      const cell = getCell(event);
      let column;
      if (cell) {
        column = getColumnByCell(table, cell);
        if (column) {
          table.$emit(`cell-${name}`, row, column, cell, event);
        }
      }
      table.$emit(`row-${name}`, row, column, event);
    },

    rowRender({ row, $index, treeRowData, treeIndent, columns, firstDefaultColumnIndex, store, columnsHidden, context, stripe, flip, currentRow, expandRows, highlightCurrentRow, rowClassName, rowStyle, rowKey, spanMethod, cellStyle, cellClassName, fixed, leftFixedLeafCount, columnsCount, rightFixedLeafCount, scrollX, disableTransition, forceExpand, hasExpandColumn, useMemo }) {
      let memo;
      const key = this.getKeyOfRow({ row, rowIndex: $index, rowKey });
      let cached;
      if (useMemo) {
        cached = this.vnodeCache[key];
        const currentSelection = store.states.selection;
        if (cached && !this.isRowSelectionChanged(row, cached.memo, currentSelection)) {
          return cached;
        }
        memo = currentSelection.slice();
      }
      const rowClasses = this.getRowClass({
        row,
        rowIndex: $index,
        stripe,
        flip,
        currentRow,
        expandRows,
        highlightCurrentRow,
        rowClassName,
        forceExpand,
        hasExpandColumn
      });
      let display = true;
      if (treeRowData) {
        rowClasses.push('zm-table__row--level-' + treeRowData.level);
        display = treeRowData.display;
        if (!display) {
          rowClasses.push('zm-table__row--hidden');
        }
      }
      const isSelectedRow = store.isSelected(row);

      const ret = (<tr
        v-show={display}
        style={this.getRowStyle({ row, rowIndex: $index, rowStyle })}
        class={rowClasses}
        key={key}
        on-dblclick={($event) => this.handleDoubleClick($event, row)}
        on-click={($event) => this.handleClick($event, row)}
        on-contextmenu={($event) => this.handleContextMenu($event, row)}
        on-mouseenter={_ => this.handleMouseEnter($index)}
        on-mouseleave={this.handleMouseLeave}
        on-keydown={($event) => this.handleKeyDown($event, $index)}
      >
        {
          columns.map((column, cellIndex) => {
            const { rowspan, colspan } = this.getSpan({
              row,
              column,
              rowIndex: $index,
              columnIndex: cellIndex,
              spanMethod,
              columnLength: columns.length
            });
            if (!rowspan || !colspan) {
              return null;
            }
            const columnData = Object.assign({}, column);
            columnData.realWidth = this.getColspanRealWidth(columns, colspan, cellIndex);
            const data = {
              store: store,
              _self: context,
              column: columnData,
              row,
              $index,
              disableTransition,
              isSelectedRow,
              flip
            };
            if (cellIndex === firstDefaultColumnIndex && treeRowData) {
              data.treeNode = {
                indent: treeRowData.level * treeIndent,
                level: treeRowData.level
              };
              if (typeof treeRowData.expanded === 'boolean') {
                data.treeNode.expanded = treeRowData.expanded;
                // 表明是懒加载
                if ('loading' in treeRowData) {
                  data.treeNode.loading = treeRowData.loading;
                }
                if ('noLazyChildren' in treeRowData) {
                  data.treeNode.noLazyChildren = treeRowData.noLazyChildren;
                }
              }
            }
            return (
              <td
                style={this.getCellStyle({
                  rowIndex: $index,
                  columnIndex: cellIndex,
                  row,
                  column: columnData,
                  cellStyle,
                  flip
                })}
                class={this.getCellClass({
                  rowIndex: $index,
                  columnIndex: cellIndex,
                  flip,
                  row,
                  column: columnData,
                  cellClassName,
                  fixed,
                  leftFixedLeafCount,
                  columnsCount,
                  rightFixedLeafCount,
                  scrollX,
                  columnsHidden
                })}
                data-index={cellIndex}
                rowspan={rowspan}
                colspan={colspan}
                role='cell'
                aria-describedby={flip ? null : column.id}
                on-mouseenter={($event) => this.handleCellMouseEnter($event, row)}
                on-mouseleave={this.handleCellMouseLeave}>
                {
                  column.renderCell.call(
                    this._renderProxy,
                    this.$createElement,
                    data
                  )
                }
              </td>
            );
          })
        }
      </tr>);
      if (useMemo && columns.length) {
        ret.memo = memo;
        this.vnodeCache[key] = ret;
      }
      return ret;
    },

    wrappedRowRender({ row, $index, store, isRowExpanded, assertRowKey, treeData, lazyTreeNodeMap, childrenColumnName, rowKey, treeIndent, columns, firstDefaultColumnIndex, columnsHidden, context, stripe, flip, currentRow, expandRows, highlightCurrentRow, rowClassName, rowStyle, spanMethod, cellStyle, cellClassName, renderExpanded, fixed, leftFixedLeafCount, columnsCount, rightFixedLeafCount, scrollX, hasExpandColumn, disableTransition, forceExpand, useMemo }) {
      if (hasExpandColumn && isRowExpanded(row)) {
        const tr = this.rowRender({
          row,
          $index,
          treeIndent,
          columns,
          firstDefaultColumnIndex,
          store,
          columnsHidden,
          context,
          stripe,
          flip,
          currentRow,
          expandRows,
          highlightCurrentRow,
          rowClassName,
          rowKey,
          rowStyle,
          spanMethod,
          cellStyle,
          cellClassName,
          fixed,
          leftFixedLeafCount,
          columnsCount,
          rightFixedLeafCount,
          scrollX,
          disableTransition,
          forceExpand,
          hasExpandColumn,
          useMemo
        });
        if (!renderExpanded) {
          console.error('[Zoom-ui Error]renderExpanded is required.');
          return tr;
        }
        // 使用二维数组，避免修改 $index
        if (!fixed) {
          const cls = forceExpand ? '' : 'zm-table__expanded-cell';
          return [[
            tr,
            <tr
              role="menu"
              class="zm-table__expanded-tr"
              key={'expanded-row__' + tr.key}>
              {
                this.renderTD(forceExpand)
              }
              <td colspan={columnsCount} class={cls}>
                {renderExpanded(this.$createElement, { row, $index, store: this.store })}
              </td>
            </tr>]];
        }
        return [[
          tr,
          <tr class="zm-table__expanded-tr--fixed" key={'expanded-row__' + tr.key}>
            <td></td>
          </tr>]];
      } else if (Object.keys(treeData).length) {
        assertRowKey();
        // TreeTable 时，rowKey 必须由用户设定，不使用 getKeyOfRow 计算
        // 在调用 rowRender 函数时，仍然会计算 rowKey，不太好的操作
        const key = getRowIdentity(row, rowKey);
        let cur = treeData[key];
        let treeRowData = null;
        if (cur) {
          treeRowData = {
            expanded: cur.expanded,
            level: cur.level,
            display: true
          };
          if (typeof cur.lazy === 'boolean') {
            if (typeof cur.loaded === 'boolean' && cur.loaded) {
              treeRowData.noLazyChildren = !(cur.children && cur.children.length);
            }
            treeRowData.loading = cur.loading;
          }
        }
        const tmp = [this.rowRender({
          row,
          $index,
          treeRowData,
          treeIndent,
          columns,
          firstDefaultColumnIndex,
          store,
          columnsHidden,
          context,
          stripe,
          flip,
          currentRow,
          expandRows,
          highlightCurrentRow,
          rowClassName,
          rowKey,
          rowStyle,
          spanMethod,
          cellStyle,
          cellClassName,
          fixed,
          leftFixedLeafCount,
          columnsCount,
          rightFixedLeafCount,
          scrollX,
          disableTransition,
          forceExpand,
          useMemo
        })];
        // 渲染嵌套数据
        if (cur) {
          // currentRow 记录的是 index，所以还需主动增加 TreeTable 的 index
          let i = 0;
          const traverse = (children, parent) => {
            if (!(children && children.length && parent)) return;
            children.forEach(node => {
              // 父节点的 display 状态影响子节点的显示状态
              const innerTreeRowData = {
                display: parent.display && parent.expanded,
                level: parent.level + 1
              };
              const childKey = getRowIdentity(node, rowKey);
              if (childKey === undefined || childKey === null) {
                throw new Error('for nested data item, row-key is required.');
              }
              cur = Object.assign({}, treeData[childKey]);
              // 对于当前节点，分成有无子节点两种情况。
              // 如果包含子节点的，设置 expanded 属性。
              // 对于它子节点的 display 属性由它本身的 expanded 与 display 共同决定。
              if (cur) {
                innerTreeRowData.expanded = cur.expanded;
                // 懒加载的某些节点，level 未知
                cur.level = cur.level || innerTreeRowData.level;
                cur.display = !!(cur.expanded && innerTreeRowData.display);
                if (typeof cur.lazy === 'boolean') {
                  if (typeof cur.loaded === 'boolean' && cur.loaded) {
                    innerTreeRowData.noLazyChildren = !(cur.children && cur.children.length);
                  }
                  innerTreeRowData.loading = cur.loading;
                }
              }
              i++;
              tmp.push(this.rowRender({
                row: node,
                $index: $index + i,
                treeRowData: innerTreeRowData,
                treeIndent,
                columns,
                firstDefaultColumnIndex,
                store,
                columnsHidden,
                context,
                stripe,
                flip,
                currentRow,
                expandRows,
                highlightCurrentRow,
                rowClassName,
                rowKey,
                rowStyle,
                spanMethod,
                cellStyle,
                cellClassName,
                fixed,
                leftFixedLeafCount,
                columnsCount,
                rightFixedLeafCount,
                scrollX,
                disableTransition,
                forceExpand,
                useMemo
              }));
              if (cur) {
                const nodes = lazyTreeNodeMap[childKey] || node[childrenColumnName];
                traverse(nodes, cur);
              }
            });
          };
          // 对于 root 节点，display 一定为 true
          cur.display = true;
          const nodes = lazyTreeNodeMap[key] || row[childrenColumnName];
          traverse(nodes, cur);
        }
        return tmp;
      } else {
        return this.rowRender({
          row,
          $index,
          treeIndent,
          columns,
          firstDefaultColumnIndex,
          store,
          columnsHidden,
          context,
          stripe,
          flip,
          currentRow,
          expandRows,
          highlightCurrentRow,
          rowClassName,
          rowKey,
          rowStyle,
          spanMethod,
          cellStyle,
          cellClassName,
          fixed,
          leftFixedLeafCount,
          columnsCount,
          rightFixedLeafCount,
          scrollX,
          disableTransition,
          forceExpand,
          useMemo
        });
      }
    },

    renderTD(forceExpand) {
      if (forceExpand) {
        return new Array(forceExpand.col || 1).fill({}).map(() => {
          return (<td></td>);
        });
      }
    }
  }
};
